import type { Axis, MoveMessage } from './net/protocol';

const FORWARD = new Set(['KeyW', 'ArrowUp']);
const BACK = new Set(['KeyS', 'ArrowDown']);
const LEFT = new Set(['KeyA', 'ArrowLeft']);
const RIGHT = new Set(['KeyD', 'ArrowRight']);
const RUN = new Set(['ShiftLeft', 'ShiftRight']);
const ALL = new Set([...FORWARD, ...BACK, ...LEFT, ...RIGHT, ...RUN]);

const any = (held: ReadonlySet<string>, keys: ReadonlySet<string>): boolean =>
  [...keys].some((k) => held.has(k));

/** Pure mapping from held keys to movement controls. Left turn is positive (CCW, Z-up). */
export function controlsFromKeys(held: ReadonlySet<string>): Omit<MoveMessage, 'type'> {
  const forward = (Number(any(held, FORWARD)) - Number(any(held, BACK))) as Axis;
  const turn = (Number(any(held, LEFT)) - Number(any(held, RIGHT))) as Axis;
  return { forward, turn, run: any(held, RUN) };
}

/**
 * Keyboard → `move` messages. Sends only when the control state changes: the
 * protocol carries state, not key repeats.
 */
export class KeyboardControls {
  private readonly held = new Set<string>();
  private last = '';

  constructor(
    private readonly send: (move: MoveMessage) => void,
    private readonly target: Window = window,
  ) {
    target.addEventListener('keydown', this.onKey);
    target.addEventListener('keyup', this.onKey);
    target.addEventListener('blur', this.onBlur);
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKey);
    this.target.removeEventListener('keyup', this.onKey);
    this.target.removeEventListener('blur', this.onBlur);
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (!ALL.has(e.code) || swallowsKey(e.target instanceof HTMLElement ? e.target : null, e.code)) return;
    if (e.type === 'keydown') this.held.add(e.code);
    else this.held.delete(e.code);
    e.preventDefault();
    this.flush();
  };

  /** Releasing focus (alt-tab) would otherwise leave us walking forever. */
  private readonly onBlur = (): void => {
    this.held.clear();
    this.flush();
  };

  private flush(): void {
    const controls = controlsFromKeys(this.held);
    const key = `${controls.forward}|${controls.turn}|${controls.run}`;
    if (key === this.last) return;
    this.last = key;
    this.send({ type: 'move', ...controls });
  }
}

/** Input types that never take typed letters. */
const NON_TEXT_INPUTS = new Set(['range', 'checkbox', 'radio', 'button', 'submit', 'reset', 'color', 'file']);

/**
 * Whether a movement key pressed on `target` belongs to that control rather than to walking.
 * Text fields keep every key (W is a letter in the chat box). A slider or checkbox only keeps
 * the arrow keys it uses itself: after nudging the volume slider, W must still walk. (The
 * chat box swallowing WASD after login was a real bug once; this is the same trap.)
 */
export function swallowsKey(
  target: { tagName: string; type?: string; isContentEditable?: boolean } | null,
  code: string,
): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  if (NON_TEXT_INPUTS.has((target.type ?? 'text').toLowerCase())) return code.startsWith('Arrow');
  return true;
}

/**
 * Combines movement from several inputs (keyboard, the touch pad) into the one control
 * state the protocol carries, and sends it only when it changes. Axes add up and are
 * clamped, so a tablet with a keyboard can use both; running if any source runs.
 */
export class MoveMixer {
  private readonly sources = new Map<string, Omit<MoveMessage, 'type'>>();
  private last = '';

  constructor(private readonly send: (move: MoveMessage) => void) {}

  set(source: string, controls: Omit<MoveMessage, 'type'>): void {
    this.sources.set(source, controls);
    const all = [...this.sources.values()];
    const clampAxis = (v: number): Axis => (v > 0 ? 1 : v < 0 ? -1 : 0);
    const merged: MoveMessage = {
      type: 'move',
      forward: clampAxis(all.reduce((sum, c) => sum + c.forward, 0)),
      turn: clampAxis(all.reduce((sum, c) => sum + c.turn, 0)),
      run: all.some((c) => c.run === true),
    };
    const key = `${merged.forward}|${merged.turn}|${merged.run}`;
    if (key === this.last) return;
    this.last = key;
    this.send(merged);
  }
}
